/**
 * Logistics Routing (L3) - Planner & History
 * Version: 1.0.0
 * Description:
 *   Plan delivery runs for drivers using priority-based auto-sequencing
 *   and manual overrides. View historical logs and rejection details.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  Tabs,
  Tab,
  MenuItem,
  Autocomplete,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Badge,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ImageList,
  ImageListItem
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  Map as MapIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as PendingIcon,
  LocalShipping as TruckIcon,
  PictureAsPdf as PdfIcon,
  PhotoCamera as PhotoIcon,
  Undo as UndoIcon,
  SettingsBackupRestore as RestoreIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { logisticsAPI } from '../../api';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GroupsIcon from '@mui/icons-material/Groups';
import useAuthStore from '../../store/authStore';

// --- Types ---
interface Driver {
  id: string;
  full_name: string;
  vehicle_number: string;
}

interface Customer {
  id: string;
  unique_id: string;
  brand_name: string;
  vehicle_no?: string;
  priority: number;
}

interface Depot {
  id: string;
  name: string;
  location: string;
}

interface PlannedStop {
  id: string; // Internal temp ID for UI
  customer_id?: string;
  unique_id: string;
  brand_name: string;
  vehicle_no: string;
  priority: number;
  sequence_order: string | number;
  is_manual: boolean;
  dispatch_order_numbers?: string;
}

interface Run {
  id: string;
  run_date: string;
  driver_name: string;
  vehicle_number: string;
  status: string;
  total_deliveries: number;
  completed_deliveries: number;
  rejected_deliveries: number;
  items?: any[];
}

interface VehicleAssignment {
  driverId: string;
  startDepotId: string;
  endDepotId: string;
}

// --- Component ---
export default function LogisticsRouting() {
  const { enqueueSnackbar } = useSnackbar();
  const user = useAuthStore((state) => state.user);
  const isAdminOrManager = user?.role === 'Admin' || user?.role === 'Manager';
  const [activeTab, setActiveTab] = useState(0);

  // Shared State
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);

  // Planner State
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [runDate, setRunDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [plannedStops, setPlannedStops] = useState<PlannedStop[]>([]);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [depots, setDepots] = useState<Depot[]>([]);
  const [isGenerated, setIsGenerated] = useState(false);
  const [vehicleAssignments, setVehicleAssignments] = useState<Record<string, VehicleAssignment>>({});
  const [defaultDepotId, setDefaultDepotId] = useState<string>('');
  
  // Planned State (Drafts)
  const [plannedRuns, setPlannedRuns] = useState<Run[]>([]);
  const [restoringRunId, setRestoringRunId] = useState<string | null>(null);
  const [editRun, setEditRun] = useState<Run | null>(null);
  const [editData, setEditData] = useState<Partial<VehicleAssignment>>({});

  // History State
  const [historyRuns, setHistoryRuns] = useState<Run[]>([]);
  const [historyDate, setHistoryDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [detailRun, setDetailRun] = useState<Run | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [viewPhotos, setViewPhotos] = useState<{ open: boolean, photos: string[], title: string }>({ open: false, photos: [], title: '' });

  // New Routing Features
  const [bulkInput, setBulkInput] = useState('');
  const [activeVehicleTab, setActiveVehicleTab] = useState('ALL');

  // Init Data
  useEffect(() => {
    const init = async () => {
      try {
        const [driversData, customersData, depotsData] = await Promise.all([
          logisticsAPI.getDrivers(),
          logisticsAPI.getCustomers({ limit: 500 }),
          logisticsAPI.listDepots()
        ]);
        setDrivers(driversData || []);
        setAllCustomers(customersData.customers || []);
        setDepots(depotsData || []);

        // Default Depot Logic: Use the depot marked as is_default
        if (depotsData && depotsData.length > 0) {
          const defaultDepot = depotsData.find((d: any) => d.is_default) || 
                             depotsData.find((d: any) => d.name.toLowerCase().includes('chennai main')) ||
                             depotsData[0];

          setDefaultDepotId(defaultDepot.id);
        }
      } catch (error) {
        enqueueSnackbar('Failed to load initial data', { variant: 'error' });
      }
    };
    init();
  }, [enqueueSnackbar]);

  // Fetch History
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await logisticsAPI.listRuns({ run_date: historyDate });
      setHistoryRuns(data || []);
    } catch (error) {
      enqueueSnackbar('Failed to fetch run history', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [historyDate, enqueueSnackbar]);

  const handleDeleteRun = async () => {
    if (!confirmDeleteId) return;
    try {
      setLoading(true);
      await logisticsAPI.deleteRun(confirmDeleteId);
      enqueueSnackbar('Run deleted successfully', { variant: 'success' });
      setHistoryRuns(prev => prev.filter(r => r.id !== confirmDeleteId));
      setConfirmDeleteId(null);
    } catch (error) {
      enqueueSnackbar('Failed to delete run', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) fetchPlanned();
    if (activeTab === 2) fetchHistory();
  }, [activeTab, fetchHistory]);

  const fetchPlanned = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both draft and assigned (pushed) runs for this screen
      const [drafts, assigned] = await Promise.all([
        logisticsAPI.listRuns({ status: 'draft', run_date: historyDate }),
        logisticsAPI.listRuns({ status: 'assigned', run_date: historyDate })
      ]);
      setPlannedRuns([...(drafts || []), ...(assigned || [])]);
    } catch (error) {
      enqueueSnackbar('Failed to fetch planned trips', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [historyDate, enqueueSnackbar]);

  const handleDownloadPDF = async (runId: string, runDate: string, vehicleNo: string) => {
    try {
      const blob = await logisticsAPI.getRunPDF(runId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `delivery_list_${dayjs(runDate).format('YYYY-MM-DD')}_${vehicleNo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    }
  };

  // Auto-populate vehicle assignments with default depot
  useEffect(() => {
    if (depots.length > 0 && defaultDepotId) {
      const newAssignments = { ...vehicleAssignments };
      let changed = false;
      
      // Get all vehicles currently in the stops list + existing groups
      const allVehicles = Array.from(new Set([
        ...plannedStops.map(s => s.vehicle_no || 'Unassigned'),
        ...Object.keys(vehicleAssignments)
      ]));

      allVehicles.forEach(v => {
        if (!newAssignments[v]) {
          newAssignments[v] = { driverId: '', startDepotId: defaultDepotId, endDepotId: defaultDepotId };
          changed = true;
        } else if (!newAssignments[v].startDepotId) {
          newAssignments[v].startDepotId = defaultDepotId;
          newAssignments[v].endDepotId = defaultDepotId;
          changed = true;
        }
      });
      if (changed) setVehicleAssignments(newAssignments);
    }
  }, [depots, defaultDepotId, plannedStops]);
  const vehicleGroups = Array.from(new Set(plannedStops.map(s => s.vehicle_no || 'Unassigned'))).sort();
  const filteredStops = activeVehicleTab === 'ALL' 
    ? plannedStops 
    : plannedStops.filter(s => (s.vehicle_no || 'Unassigned') === activeVehicleTab);

  // Initialize assignments when vehicle groups change
  useEffect(() => {
    setVehicleAssignments(prev => {
      const next = { ...prev };
      vehicleGroups.forEach(v => {
        if (!next[v]) {
          next[v] = { driverId: '', startDepotId: defaultDepotId, endDepotId: defaultDepotId };
        } else if (defaultDepotId && !next[v].startDepotId) {
          // If defaultDepotId just loaded, apply it
          next[v].startDepotId = defaultDepotId;
          next[v].endDepotId = defaultDepotId;
        }
      });
      return next;
    });
  }, [vehicleGroups, defaultDepotId]);

  const resequencePerVehicle = (stops: PlannedStop[]): PlannedStop[] => {
    const updated = [...stops];
    // Sort all stops first purely by their position (float)
    updated.sort((a, b) => {
      if (a.vehicle_no !== b.vehicle_no) return a.vehicle_no.localeCompare(b.vehicle_no);
      const seqA = parseFloat(String(a.sequence_order || 0));
      const seqB = parseFloat(String(b.sequence_order || 0));
      if (seqA !== seqB) return seqA - seqB;
      return (a.priority || 999) - (b.priority || 999);
    });

    const vehicleCounts: Record<string, number> = {};
    return updated.map(s => {
      const v = s.vehicle_no || 'Unassigned';
      const seqStr = String(s.sequence_order || '');
      
      // If it has a decimal, we preserve it as is
      if (seqStr.includes('.')) {
        return { ...s };
      }
      
      // If it's a whole number, we re-index to fill gaps
      vehicleCounts[v] = (vehicleCounts[v] || 0) + 1;
      return { ...s, sequence_order: vehicleCounts[v] };
    });
  };

  // Planner Handlers
  const handleAddStop = (customer: Customer | null) => {
    if (!customer) return;
    
    // Check if duplicate?
    if (plannedStops.some(s => s.unique_id === customer.unique_id)) {
      enqueueSnackbar('Customer already added to this run', { variant: 'warning' });
      return;
    }

    const newStop: PlannedStop = {
      id: Math.random().toString(36).substr(2, 9),
      customer_id: customer.id,
      unique_id: customer.unique_id,
      brand_name: customer.brand_name,
      vehicle_no: customer.vehicle_no || '',
      priority: customer.priority,
      sequence_order: 0,
      is_manual: false,
      dispatch_order_numbers: ''
    };

    setPlannedStops(resequencePerVehicle([...plannedStops, newStop]));
    setIsGenerated(false); // Reset on change
  };

  const handleBulkAdd = () => {
    if (!bulkInput.trim()) return;
    
    // Split by comma, space, or newline
    const ids = bulkInput.split(/[,\s\n]+/).map(id => id.trim().toUpperCase()).filter(Boolean);
    const matched: PlannedStop[] = [];
    const notFound: string[] = [];

    ids.forEach(id => {
      const customer = allCustomers.find(c => c.unique_id.toUpperCase() === id);
      if (customer) {
        if (!plannedStops.some(s => s.unique_id === customer.unique_id) && !matched.some(m => m.unique_id === customer.unique_id)) {
           matched.push({
             id: Math.random().toString(36).substr(2, 9),
             customer_id: customer.id,
             unique_id: customer.unique_id,
             brand_name: customer.brand_name,
             vehicle_no: customer.vehicle_no || '',
             priority: customer.priority,
             sequence_order: 0,
             is_manual: false,
             dispatch_order_numbers: ''
           });
        }
      } else {
        notFound.push(id);
      }
    });

    if (matched.length > 0) {
      setPlannedStops(resequencePerVehicle([...plannedStops, ...matched]));
      setBulkInput('');
      enqueueSnackbar(`Added ${matched.length} customers.`, { variant: 'success' });
    }

    if (notFound.length > 0) {
      enqueueSnackbar(`IDs not found: ${notFound.slice(0, 3).join(', ')}${notFound.length > 3 ? '...' : ''}`, { variant: 'warning' });
    }
    setIsGenerated(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws) as any[];

        // Extract IDs from any column that looks like 'id' or 'unique_id'
        const importedMatched: PlannedStop[] = [];
        data.forEach((row, idx) => {
          const idValue = row.unique_id || row.id || row.customer_id || Object.values(row)[0];
          if (idValue) {
            const id = String(idValue).trim().toUpperCase();
            const customer = allCustomers.find(c => c.unique_id.toUpperCase() === id);
            if (customer && !importedMatched.some(m => m.unique_id === customer.unique_id)) {
              importedMatched.push({
                id: Math.random().toString(36).substr(2, 9),
                customer_id: customer.id,
                unique_id: customer.unique_id,
                brand_name: customer.brand_name,
                vehicle_no: customer.vehicle_no || '',
                priority: customer.priority,
                sequence_order: 0,
                is_manual: false,
                dispatch_order_numbers: ''
              });
            }
          }
        });

        if (importedMatched.length > 0) {
          // Rule: Clear list on upload
          setPlannedStops(resequencePerVehicle(importedMatched));
          enqueueSnackbar(`Imported ${importedMatched.length} stops from file.`, { variant: 'success' });
        } else {
          enqueueSnackbar('No valid customer IDs found in file.', { variant: 'error' });
        }
      } catch (err) {
        enqueueSnackbar('Failed to parse Excel file.', { variant: 'error' });
      }
    };
    reader.readAsBinaryString(file);
    setIsGenerated(false);
    // Reset file input
    e.target.value = '';
  };

  const handleGenerateSequence = () => {
    if (plannedStops.length === 0) {
       enqueueSnackbar('No stops to sequence', { variant: 'warning' });
       return;
    }
    setPlannedStops(resequencePerVehicle(plannedStops));
    setIsGenerated(true);
    enqueueSnackbar('List generated and sorted! You can now save trip.', { variant: 'info' });
  };

  const handleRecallRun = async (runId: string) => {
    try {
      setLoading(true);
      await logisticsAPI.updateRunStatus(runId, 'draft');
      enqueueSnackbar('Trip recalled to drafts!', { variant: 'info' });
      fetchPlanned();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to recall trip', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreToPlanner = async (run: Run) => {
    try {
      setLoading(true);
      // Ensure we have full details including items
      const run_resp = await logisticsAPI.listRuns({ id: run.id });
      const fullRun = run_resp.items?.[0] || run;
      
      const stops: PlannedStop[] = (fullRun.items || []).map((item: any) => ({
        id: item.id,
        customer_id: item.customer_id,
        unique_id: item.unique_id || 'MANUAL',
        brand_name: item.brand_name || item.customer_details?.brand_name || item.manual_data?.brand_name || 'N/A',
        vehicle_no: run.vehicle_number, 
        priority: item.customer_details?.priority || 999,
        sequence_order: item.sequence_order,
        is_manual: !item.customer_id,
        dispatch_order_numbers: item.dispatch_order_numbers
      }));
      
      setPlannedStops(stops);
      setRunDate(run.run_date);
      setVehicleAssignments({
        ...vehicleAssignments,
        [run.vehicle_number]: {
            driverId: (run as any).driver_id || '',
            startDepotId: (run as any).start_depot_id || defaultDepotId,
            endDepotId: (run as any).end_depot_id || defaultDepotId
        }
      });
      setActiveVehicleTab(run.vehicle_number);
      setRestoringRunId(run.id);
      setIsGenerated(true);
      setActiveTab(0); // Switch to Planner
      enqueueSnackbar(`Trip loaded into Planner. Note: Saving will REPLACE the original trip`, { variant: 'info' });
    } catch (error: any) {
      enqueueSnackbar('Failed to load run details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleManualAdd = () => {
    const newStop: PlannedStop = {
      id: Math.random().toString(36).substr(2, 9),
      unique_id: 'MANUAL',
      brand_name: 'Custom Entry',
      vehicle_no: 'Manual',
      priority: 999,
      sequence_order: 0,
      is_manual: true,
      dispatch_order_numbers: ''
    };
    setPlannedStops(resequencePerVehicle([...plannedStops, newStop]));
  };

  const handleUpdateSequence = (id: string, val: string) => {
    // 1. Instantly update the value in our local list WITHOUT sorting/shifting
    setPlannedStops(prev => prev.map(s => s.id === id ? { ...s, sequence_order: val } : s));
  };

  const handleCommitSequence = (id: string) => {
    const targetStop = plannedStops.find(s => s.id === id);
    if (!targetStop) return;

    const valStr = String(targetStop.sequence_order || '');
    if (valStr === '' || valStr === '0') {
        setPlannedStops(resequencePerVehicle(plannedStops));
        return;
    }

    const newSeqNum = parseFloat(valStr);
    if (isNaN(newSeqNum)) return;

    // Perform the smart shift logic
    const allStops = [...plannedStops];
    const vehicleKey = targetStop.vehicle_no || 'Unassigned';

    // Filter others of the same vehicle
    const othersInVehicle = allStops
      .filter(s => (s.vehicle_no || 'Unassigned') === vehicleKey && s.id !== id)
      .sort((a,b) => parseFloat(String(a.sequence_order || 0)) - parseFloat(String(b.sequence_order || 0)));

    // Insertion: Find the index where this new value belongs numerically
    // For integers like '5', it's idx 4. For '1.1', it's idx 1.
    // simpler: splice and then let resequencePerVehicle handle the re-indexing
    const targetIdx = othersInVehicle.findIndex(s => parseFloat(String(s.sequence_order || 0)) >= newSeqNum);
    const spliceIdx = targetIdx === -1 ? othersInVehicle.length : targetIdx;
    
    othersInVehicle.splice(spliceIdx, 0, targetStop);

    // Re-index only this vehicle group
    const reindexed = resequencePerVehicle(othersInVehicle);
    const otherVehicles = allStops.filter(s => (s.vehicle_no || 'Unassigned') !== vehicleKey);
    
    setPlannedStops([...otherVehicles, ...reindexed]);
    setIsGenerated(false);
  };

  const handleRemoveStop = (id: string) => {
    setPlannedStops(resequencePerVehicle(plannedStops.filter(s => s.id !== id)));
    setIsGenerated(false); // Reset on change
  };

  const handlePlanRun = async () => {
    if (activeVehicleTab === 'ALL') {
      enqueueSnackbar('Please select a specific vehicle tab to push the route', { variant: 'info' });
      return;
    }

    const assignment = vehicleAssignments[activeVehicleTab];
    if (!assignment?.driverId) {
      enqueueSnackbar(`Please assign a driver for ${activeVehicleTab}`, { variant: 'error' });
      return;
    }
    
    // Filter by active tab for pushing
    const stopsToPush = plannedStops.filter(s => (s.vehicle_no || 'Unassigned') === activeVehicleTab);

    if (stopsToPush.length === 0) {
      enqueueSnackbar(`No stops in ${activeVehicleTab} to push`, { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      if (restoringRunId) {
        // If we are restoring, we DELETE the old run and create a new one to avoid ID confusion
        // or we could support a 'PATCH' endpoint for items. Creating new is safer for sequencing.
        await logisticsAPI.deleteRun(restoringRunId);
      }

      await logisticsAPI.planRun({
        driver_id: assignment.driverId,
        run_date: runDate,
        start_depot_id: assignment.startDepotId,
        end_depot_id: assignment.endDepotId,
        items: stopsToPush.map((s) => ({
          customer_id: s.customer_id,
          unique_id: s.unique_id === 'MANUAL' ? undefined : s.unique_id,
          manual_data: s.is_manual ? { brand_name: s.brand_name } : undefined,
          sequence_order: typeof s.sequence_order === 'string' ? parseFloat(s.sequence_order) : s.sequence_order,
          dispatch_order_numbers: s.dispatch_order_numbers
        }))
      });
      enqueueSnackbar(`Trip saved for ${activeVehicleTab}! Review it in "Planned Trips".`, { variant: 'success' });
      
      // Clear only the pushed stops
      setPlannedStops(plannedStops.filter(s => !stopsToPush.includes(s)));
      setIsGenerated(false);
      setRestoringRunId(null);
      setActiveVehicleTab('ALL');
      fetchPlanned(); // Refresh planned tab
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to sync run', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };



  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
        Logistics Routing & Planner
      </Typography>
      
      <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)} sx={{ mb: 3 }}>
        <Tab label="1. Planner" icon={<MapIcon />} iconPosition="start" />
        <Tab label="2. Planned Trips" icon={<SuccessIcon />} iconPosition="start" />
        <Tab label="3. Trip History" icon={<HistoryIcon />} iconPosition="start" />
      </Tabs>

      {activeTab === 0 ? (
        /* Planner UI */
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>1. Assign Trip</Typography>
              <Stack spacing={3} mt={2}>
                 <TextField
                  label="Trip Date"
                  type="date"
                  fullWidth
                  value={runDate}
                  onChange={(e) => setRunDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                <Divider />
                <Typography variant="subtitle2">Batch Input</Typography>
                <TextField 
                  multiline rows={3} 
                  placeholder="Paste Unique IDs (LOG-01, LOG-02...)"
                  size="small"
                  value={bulkInput}
                  onChange={(e) => setBulkInput(e.target.value)}
                />
                <Button variant="outlined" size="small" onClick={handleBulkAdd}>Process IDs</Button>
                
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  color="info"
                  size="small"
                >
                  Upload Route (.xlsx)
                  <input type="file" hidden accept=".xlsx, .xls, .csv" onChange={handleFileUpload} />
                </Button>

                <Divider />
                
                <Stack spacing={2}>
                  {!isGenerated ? (
                    <Button 
                      fullWidth 
                      variant="contained" 
                      color="secondary"
                      size="large"
                      startIcon={<TruckIcon />}
                      onClick={handleGenerateSequence}
                      disabled={loading || filteredStops.length === 0}
                    >
                      Step 1: Generate List
                    </Button>
                  ) : (
                    <>
                      <Button 
                        fullWidth 
                        variant="contained" 
                        color="success"
                        size="large"
                        startIcon={<SaveIcon />}
                        onClick={handlePlanRun}
                        disabled={loading || activeVehicleTab === 'ALL' || !vehicleAssignments[activeVehicleTab]?.driverId}
                      >
                        Step 2: Save Trip
                      </Button>
                      <Button 
                        fullWidth 
                        variant="text" 
                        size="small"
                        onClick={() => setIsGenerated(false)}
                      >
                        Edit List (Go Back)
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <Box p={1} sx={{ backgroundColor: 'rgba(0,0,0,0.02)', borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={activeVehicleTab} 
                  onChange={(_, val) => setActiveVehicleTab(val)} 
                  variant="scrollable" 
                  scrollButtons="auto"
                >
                  <Tab label="ALL" value="ALL" icon={<GroupsIcon />} iconPosition="start" />
                  {vehicleGroups.map(v => (
                    <Tab 
                      key={v} 
                      label={v} 
                      value={v} 
                      icon={<TruckIcon sx={{ fontSize: 16 }} />} 
                      iconPosition="start" 
                    />
                  ))}
                </Tabs>
              </Box>

              {/* Per-Vehicle Assignment Controls */}
              {activeVehicleTab !== 'ALL' && isGenerated && (
                <Box p={2} sx={{ backgroundColor: 'primary.main', color: 'white' }}>
                   <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                         <TextField
                            select
                            fullWidth
                            size="small"
                             label="Assign Driver"
                             value={vehicleAssignments[activeVehicleTab]?.driverId || ''}
                             onChange={(e) => setVehicleAssignments({
                               ...vehicleAssignments,
                               [activeVehicleTab]: { ...vehicleAssignments[activeVehicleTab], driverId: e.target.value }
                             })}
                             sx={{ backgroundColor: 'white', borderRadius: 1 }}
                             InputLabelProps={{ shrink: true }}
                          >
                            {drivers.map(d => (
                               <MenuItem key={d.id} value={d.id}>{d.full_name} ({d.vehicle_number})</MenuItem>
                            ))}
                         </TextField>
                      </Grid>
                      <Grid item xs={6} md={3}>
                         <TextField
                            select
                            fullWidth
                            size="small"
                            label="Start Point"
                            variant="filled"
                            value={vehicleAssignments[activeVehicleTab]?.startDepotId || ''}
                            onChange={(e) => setVehicleAssignments({
                              ...vehicleAssignments,
                              [activeVehicleTab]: { ...vehicleAssignments[activeVehicleTab], startDepotId: e.target.value }
                            })}
                            sx={{ backgroundColor: 'white', borderRadius: 1 }}
                         >
                            {depots.map(d => (
                               <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                            ))}
                         </TextField>
                      </Grid>
                      <Grid item xs={6} md={3}>
                         <TextField
                            select
                            fullWidth
                            size="small"
                            label="End Point"
                            variant="filled"
                            value={vehicleAssignments[activeVehicleTab]?.endDepotId || ''}
                            onChange={(e) => setVehicleAssignments({
                              ...vehicleAssignments,
                              [activeVehicleTab]: { ...vehicleAssignments[activeVehicleTab], endDepotId: e.target.value }
                            })}
                            sx={{ backgroundColor: 'white', borderRadius: 1 }}
                         >
                            {depots.map(d => (
                               <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                            ))}
                         </TextField>
                      </Grid>
                      <Grid item xs={12} md={2}>
                          <Button 
                             variant="contained" 
                             color="success" 
                             fullWidth 
                             onClick={handlePlanRun}
                             disabled={loading || !vehicleAssignments[activeVehicleTab]?.driverId}
                          >
                             Save Trip
                          </Button>
                      </Grid>
                   </Grid>
                </Box>
              )}
              <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={40}>S.No</TableCell>
                      <TableCell width={70}>Seq</TableCell>
                      <TableCell width={70}>ID</TableCell>
                      <TableCell>Customer Name</TableCell>
                      <TableCell width={70}>Vehicle</TableCell>
                      <TableCell width={70}>Priority</TableCell>
                      <TableCell width={160}>B2C Orders</TableCell>
                      <TableCell width={40} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Start Depot Row */}
                     {activeVehicleTab !== 'ALL' && vehicleAssignments[activeVehicleTab]?.startDepotId && (
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell><Chip label="START" color="primary" size="small" variant="outlined" /></TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {depots.find(d => d.id === vehicleAssignments[activeVehicleTab].startDepotId)?.name || 'Depot'}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    )}

                    {filteredStops.map((stop, idx) => (
                      <TableRow key={stop.id} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField
                            type="text"
                            size="small"
                            value={stop.sequence_order || ''}
                            onChange={(e) => handleUpdateSequence(stop.id, e.target.value)}
                            onBlur={() => handleCommitSequence(stop.id)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleCommitSequence(stop.id);
                                    (e.target as any).blur();
                                }
                            }}
                            sx={{ 
                              width: 55,
                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0
                              },
                              '& input[type=number]': {
                                '-moz-appearance': 'textfield'
                              }
                            }}
                            InputProps={{ sx: { height: 30, fontSize: '0.875rem' } }}
                          />
                        </TableCell>
                        <TableCell>
                           <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>{stop.unique_id}</Typography>
                        </TableCell>
                        <TableCell>
                          {stop.is_manual ? (
                            <TextField 
                               size="small" 
                               value={stop.brand_name} 
                               fullWidth
                               onChange={(e) => {
                                 const updated = [...plannedStops];
                                 const s = updated.find(x => x.id === stop.id);
                                 if (s) s.brand_name = e.target.value;
                                 setPlannedStops(updated);
                               }}
                               InputProps={{ sx: { height: 30, fontSize: '0.875rem' } }}
                            />
                          ) : (
                            <Typography variant="body2">{stop.brand_name}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                           <Chip 
                              label={stop.vehicle_no || 'None'} 
                              size="small" 
                              variant="outlined" 
                              sx={{ fontSize: '0.7rem', height: 20 }} 
                           />
                        </TableCell>
                        <TableCell>
                           <Chip 
                              label={`P-${stop.priority}`} 
                              size="small" 
                              color={stop.priority >= 10 ? 'error' : 'default'}
                              sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} 
                           />
                        </TableCell>
                        <TableCell>
                            <TextField 
                               size="small" 
                               placeholder="e.g. 17522, 17523"
                               value={stop.dispatch_order_numbers || ''} 
                               fullWidth
                               onChange={(e) => {
                                 const updated = [...plannedStops];
                                 const s = updated.find(x => x.id === stop.id);
                                 if (s) s.dispatch_order_numbers = e.target.value;
                                 setPlannedStops(updated);
                               }}
                               InputProps={{ sx: { height: 30, fontSize: '0.75rem' } }}
                            />
                         </TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => handleRemoveStop(stop.id)} color="error" size="small">
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* End Depot Row */}
                    {activeVehicleTab !== 'ALL' && vehicleAssignments[activeVehicleTab]?.endDepotId && (
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell><Chip label="END" color="secondary" size="small" /></TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {depots.find(d => d.id === vehicleAssignments[activeVehicleTab].endDepotId)?.name || 'Depot'}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    )}

                    {plannedStops.length === 0 && (
                       <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                            <Typography color="text.secondary">No stops added yet. Use the sidebar to plan your route.</Typography>
                          </TableCell>
                       </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      ) : activeTab === 1 ? (
        /* Planned Trips UI */
        <Box>
           <Card sx={{ p: 2, mb: 3 }}>
              <Stack direction="row" spacing={3} alignItems="center">
                 <TextField
                    label="Drafts Date"
                    type="date"
                    size="small"
                    value={historyDate}
                    onChange={(e) => setHistoryDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                 />
                 <Button variant="outlined" onClick={fetchPlanned} startIcon={<SearchIcon />}>Refresh Drafts</Button>
              </Stack>
           </Card>

           <Grid container spacing={3}>
              {plannedRuns.map((run) => (
                 <Grid item xs={12} sm={6} md={4} key={run.id}>
                    <Card sx={{ p: 2, borderRadius: 2, border: '1px dashed', borderColor: 'primary.light', position: 'relative' }}>
                       <Stack direction="row" justifyContent="space-between" mb={1}>
                          <Chip 
                             label={
                               run.status === 'ready' ? "DISPATCHED / READY" :
                               run.status === 'draft' ? "DRAFT / SAVED" : 
                               "PUSHED / ACTIVE"
                             } 
                             size="small" 
                             color={
                               run.status === 'ready' ? "warning" :
                               run.status === 'draft' ? "info" : 
                               "success"
                             } 
                             variant={run.status === 'draft' ? "outlined" : "filled"} 
                           />
                          <Typography variant="caption" color="text.secondary">{dayjs(run.run_date).format('DD MMM YYYY')}</Typography>
                       </Stack>
                       
                       <Typography variant="h6">{run.driver_name || 'Unassigned'}</Typography>
                       <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TruckIcon fontSize="inherit" /> {run.vehicle_number || 'N/A'}
                       </Typography>
                       
                       <Box sx={{ position: 'absolute', top: 55, right: 16 }}>
                          <IconButton 
                            size="small" 
                            color="primary" 
                            title="Download Delivery List"
                            onClick={() => handleDownloadPDF(run.id, run.run_date, run.vehicle_number || 'v')}
                          >
                            <PdfIcon />
                          </IconButton>
                       </Box>
                       
                       <Divider sx={{ my: 1.5 }} />
                       
                       <Typography variant="body2" gutterBottom><b>Drops:</b> {run.total_deliveries}</Typography>
                       
                       {/* Mini Trip List */}
                       <Box sx={{ mt: 1, maxHeight: 120, overflowY: 'auto', p: 1, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                          {run.items?.map((item: any, i: number) => (
                             <Box key={item.id} sx={{ mb: 0.5 }}>
                                <Typography variant="caption" display="block" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                   {i + 1}. {item.brand_name || item.customer_details?.brand_name || 'Manual Drop'}
                                </Typography>
                                {item.dispatch_order_numbers && (
                                   <Typography variant="caption" color="primary.main" sx={{ pl: 2, fontSize: '0.65rem', fontWeight: 600 }}>
                                      📦 Orders: {item.dispatch_order_numbers}
                                   </Typography>
                                )}
                             </Box>
                          ))}
                       </Box>

                       <Stack direction="row" spacing={1} mt={2}>
                           {run.status === 'draft' && (
                             <Button 
                               fullWidth 
                               variant="contained" 
                               color="success" 
                               startIcon={<SuccessIcon />}
                               onClick={async () => {
                                 try {
                                   await logisticsAPI.updateRunStatus(run.id, 'assigned');
                                   enqueueSnackbar('Trip pushed to driver app!', { variant: 'success' });
                                   fetchPlanned();
                                 } catch (e) {
                                   enqueueSnackbar('Failed to push trip', { variant: 'error' });
                                 }
                               }}
                             >
                               Push
                             </Button>
                           )}

                           {run.status === 'assigned' && (
                             <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                               <Button 
                                 fullWidth 
                                 variant="outlined" 
                                 color="info"
                                 startIcon={<UndoIcon />}
                                 onClick={() => handleRecallRun(run.id)}
                               >
                                 Recall
                               </Button>
                               <Button 
                                 fullWidth 
                                 variant="contained" 
                                 color="warning"
                                 startIcon={<PlayIcon />}
                                 onClick={async () => {
                                   try {
                                     await logisticsAPI.publishRun(run.id);
                                     enqueueSnackbar('Trip DISPATCHED! Driver can now start.', { variant: 'success' });
                                     fetchPlanned();
                                   } catch (e) {
                                     enqueueSnackbar('Failed to dispatch', { variant: 'error' });
                                   }
                                 }}
                               >
                                 Dispatch
                               </Button>
                             </Stack>
                           )}

                           {run.status === 'ready' && (
                             <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                               <Button 
                                 fullWidth 
                                 variant="outlined" 
                                 color="info"
                                 startIcon={<UndoIcon />}
                                 onClick={() => handleRecallRun(run.id)}
                               >
                                 Recall
                               </Button>
                               <Button 
                                 fullWidth 
                                 variant="contained" 
                                 color="success"
                                 startIcon={<SuccessIcon />}
                                 disabled
                               >
                                 READY
                               </Button>
                             </Stack>
                           )}
                           
                           <Button 
                              variant="outlined" 
                              startIcon={<EditIcon />}
                              disabled={!isAdminOrManager && run.status !== 'draft'} 
                              onClick={() => {
                                setEditRun(run);
                                setEditData({ driverId: (run as any).driver_id, startDepotId: (run as any).start_depot_id, endDepotId: (run as any).end_depot_id });
                              }}
                            >
                              Edit
                           </Button>

                           <IconButton 
                              variant="outlined"
                              color="primary"
                              sx={{ border: 1, borderColor: 'primary.light', borderRadius: 1 }}
                              onClick={() => handleRestoreToPlanner(run)}
                              title="Restore to Planner to edit stops"
                            >
                              <RestoreIcon />
                           </IconButton>

                           {isAdminOrManager && (
                              <IconButton 
                                color="error" 
                                disabled={!isAdminOrManager && run.status !== 'draft'} 
                                onClick={() => setConfirmDeleteId(run.id)}
                                sx={{ border: 1, borderColor: 'error.light', borderRadius: 1 }}
                              >
                                <DeleteIcon />
                              </IconButton>
                           )}
                       </Stack>
                    </Card>
                 </Grid>
              ))}
              {plannedRuns.length === 0 && !loading && (
                 <Grid item xs={12}>
                    <Paper sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                       <PendingIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                       <Typography variant="h6" color="text.disabled">No saved drafts for this date.</Typography>
                    </Paper>
                 </Grid>
              )}
           </Grid>

           {/* Quick Edit Dialog */}
           <Dialog open={!!editRun} onClose={() => setEditRun(null)}>
              <DialogTitle>Edit Trip Details</DialogTitle>
              <DialogContent dividers>
                 <Stack spacing={3} sx={{ minWidth: 300, mt: 1 }}>
                    <TextField
                      select
                      fullWidth
                      label="Driver"
                      value={editData.driverId || ''}
                      onChange={(e) => setEditData({ ...editData, driverId: e.target.value })}
                    >
                      {drivers.map(d => (
                        <MenuItem key={d.id} value={d.id}>{d.full_name} ({d.vehicle_number})</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      fullWidth
                      label="Start Depot"
                      value={editData.startDepotId || ''}
                      onChange={(e) => setEditData({ ...editData, startDepotId: e.target.value })}
                    >
                      {depots.map(d => (
                        <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      fullWidth
                      label="End Depot"
                      value={editData.endDepotId || ''}
                      onChange={(e) => setEditData({ ...editData, endDepotId: e.target.value })}
                    >
                      {depots.map(d => (
                        <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                      ))}
                    </TextField>
                 </Stack>
              </DialogContent>
              <DialogActions>
                 <Button onClick={() => setEditRun(null)}>Cancel</Button>
                 <Button variant="contained" onClick={async () => {
                    if (!editRun) return;
                    try {
                      await logisticsAPI.updateRun(editRun.id, {
                        driver_id: editData.driverId,
                        start_depot_id: editData.startDepotId,
                        end_depot_id: editData.endDepotId
                      });
                      enqueueSnackbar('Trip updated!', { variant: 'success' });
                      setEditRun(null);
                      fetchPlanned();
                    } catch (e) {
                      enqueueSnackbar('Update failed', { variant: 'error' });
                    }
                 }}>Save Changes</Button>
              </DialogActions>
           </Dialog>
        </Box>
      ) : (
        /* History UI */
        <Box>
           <Card sx={{ p: 2, mb: 3 }}>
              <Stack direction="row" spacing={3} alignItems="center">
                 <TextField
                    label="Filter Date"
                    type="date"
                    size="small"
                    value={historyDate}
                    onChange={(e) => setHistoryDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                 />
                 <Button variant="outlined" onClick={fetchHistory} startIcon={<SearchIcon />}>Search</Button>
              </Stack>
           </Card>

           <Grid container spacing={3}>
              {historyRuns.map((run) => (
                 <Grid item xs={12} sm={6} md={4} key={run.id}>
                    <Card sx={{ p: 2, borderRadius: 2, '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.1)' } }}>
                       <Stack direction="row" justifyContent="space-between" mb={1}>
                          <Chip 
                            label={run.status.toUpperCase()} 
                            size="small" 
                            color={run.status === 'completed' ? 'success' : run.status === 'in_progress' ? 'warning' : 'default'} 
                          />
                          <Typography variant="caption" color="text.secondary">{dayjs(run.run_date).format('DD MMM YYYY')}</Typography>
                       </Stack>
                       
                       <Typography variant="h6">{run.driver_name}</Typography>
                       <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TruckIcon fontSize="inherit" /> {run.vehicle_number}
                       </Typography>
                       
                       <Divider sx={{ my: 1.5 }} />
                       
                       <Grid container spacing={2}>
                          <Grid item xs={4}>
                             <Typography variant="h6" align="center">{run.total_deliveries}</Typography>
                             <Typography variant="caption" align="center" display="block">Drops</Typography>
                          </Grid>
                          <Grid item xs={4}>
                             <Typography variant="h6" align="center" color="success.main">{run.completed_deliveries}</Typography>
                             <Typography variant="caption" align="center" display="block">Done</Typography>
                          </Grid>
                          <Grid item xs={4}>
                             <Typography variant="h6" align="center" color="error.main">{run.rejected_deliveries}</Typography>
                             <Typography variant="caption" align="center" display="block">Rejected</Typography>
                          </Grid>
                       </Grid>
                       
                       <Stack direction="row" spacing={1} mt={2}>
                          <Button fullWidth size="small" variant="outlined" onClick={() => setDetailRun(run)}>Details</Button>
                          <IconButton 
                             size="small" 
                             color="secondary" 
                             onClick={() => handleDownloadPDF(run.id, run.run_date, (run as any).vehicle_number || 'v')}
                           >
                             <PdfIcon />
                           </IconButton>
                          {isAdminOrManager && (
                             <IconButton 
                               size="small" 
                               color="error" 
                               onClick={() => setConfirmDeleteId(run.id)}
                               sx={{ border: 1, borderColor: 'error.light', borderRadius: 1 }}
                             >
                                <DeleteIcon fontSize="small" />
                             </IconButton>
                          )}
                       </Stack>
                    </Card>
                 </Grid>
              ))}
              {historyRuns.length === 0 && !loading && (
                 <Grid item xs={12}>
                    <Paper sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                       <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                       <Typography variant="h6" color="text.disabled">No trips found for this date.</Typography>
                    </Paper>
                 </Grid>
              )}
           </Grid>
        </Box>
      )}

      {/* Details Dialog */}
      <Dialog open={!!detailRun} onClose={() => setDetailRun(null)} maxWidth="md" fullWidth>
         <DialogTitle>
            Trip Details: {detailRun?.driver_name} ({detailRun?.run_date})
         </DialogTitle>
         <DialogContent dividers>
            <Table size="small">
               <TableHead>
                  <TableRow>
                     <TableCell>Seq</TableCell>
                     <TableCell>Point</TableCell>
                     <TableCell>Status</TableCell>
                     <TableCell>Arrived</TableCell>
                     <TableCell>Timeline</TableCell>
                  </TableRow>
               </TableHead>
               <TableBody>
                  {detailRun?.items?.map((item: any) => (
                     <TableRow key={item.id}>
                        <TableCell>{item.sequence_order}</TableCell>
                        <TableCell>
                           <strong>{item.brand_name || 'Manual Drop'}</strong>
                           <Typography variant="caption" display="block">{item.unique_id}</Typography>
                        </TableCell>
                        <TableCell>
                           <Chip 
                             label={item.status} 
                             size="small" 
                             variant="outlined"
                             color={item.status === 'delivered' ? 'success' : item.status === 'rejected' ? 'error' : 'default'} 
                             sx={{ height: 20 }}
                           />
                        </TableCell>
                        <TableCell>{item.arrived_at ? dayjs(item.arrived_at).format('HH:mm') : '-'}</TableCell>
                        <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                               {item.pod_photo_urls?.length > 0 ? (
                                  <IconButton 
                                    size="small" 
                                    color="primary" 
                                    title={`View Proof (${item.pod_photo_urls.length} photos)`}
                                    onClick={() => setViewPhotos({ 
                                       open: true, 
                                       photos: item.pod_photo_urls, 
                                       title: item.brand_name || item.customer_details?.brand_name || 'Manual Drop' 
                                    })}
                                  >
                                    <Badge 
                                       badgeContent={item.pod_photo_urls.length} 
                                       color="secondary" 
                                       overlap="circular"
                                       sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }}
                                    >
                                       <PhotoIcon fontSize="inherit" />
                                    </Badge>
                                  </IconButton>
                               ) : (
                                  (item.status === 'delivered' || item.status === 'rejected') && (
                                     <Box title="No photos uploaded" sx={{ display: 'flex', alignItems: 'center', opacity: 0.35 }}>
                                        <PhotoIcon fontSize="small" color="disabled" />
                                     </Box>
                                  )
                               )}
                               
                               {item.status === 'delivered' && <SuccessIcon color="success" sx={{ fontSize: 18 }} />}
                               {item.status === 'rejected' && <ErrorIcon color="error" sx={{ fontSize: 18 }} />}
                            </Stack>
                         </TableCell>
                     </TableRow>
                  ))}
               </TableBody>
            </Table>
         </DialogContent>
         <DialogActions>
            <Button onClick={() => setDetailRun(null)}>Close</Button>
            <Button variant="contained" startIcon={<PdfIcon />}>Export PDF</Button>
         </DialogActions>
      </Dialog>

      {/* Photo Viewer Dialog */}
      <Dialog 
        open={viewPhotos.open} 
        onClose={() => setViewPhotos({ ...viewPhotos, open: false })} 
        maxWidth="sm" 
        fullWidth
      >
         <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography variant="h6">Proof of Delivery: {viewPhotos.title}</Typography>
            <IconButton onClick={() => setViewPhotos({ ...viewPhotos, open: false })} size="small">
               <CloseIcon />
            </IconButton>
         </DialogTitle>
         <DialogContent dividers sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
            {viewPhotos.photos.length > 0 ? (
               <ImageList cols={1} gap={16}>
                  {viewPhotos.photos.map((url, idx) => (
                     <ImageListItem key={idx} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                        <img 
                          src={url} 
                          alt={`POD ${idx + 1}`} 
                          loading="lazy" 
                          style={{ cursor: 'zoom-in', width: '100%', height: 'auto' }}
                          onClick={() => window.open(url, '_blank')}
                        />
                     </ImageListItem>
                  ))}
               </ImageList>
            ) : (
               <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No photos available for this stop.</Typography>
               </Box>
            )}
         </DialogContent>
         <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setViewPhotos({ ...viewPhotos, open: false })}>Close</Button>
         </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
         <DialogTitle>Delete Route History?</DialogTitle>
         <DialogContent>
            <Typography>
               Are you sure you want to delete this trip history for <strong>{historyRuns.find(r => r.id === confirmDeleteId)?.driver_name}</strong>?
               This will also delete all associated GPS logs and delivery photos. This action cannot be undone.
            </Typography>
         </DialogContent>
         <DialogActions sx={{ pb: 2, px: 3 }}>
            <Button onClick={() => setConfirmDeleteId(null)} disabled={loading}>Cancel</Button>
            <Button 
               onClick={handleDeleteRun} 
               color="error" 
               variant="contained" 
               disabled={loading}
               startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            >
               {loading ? 'Deleting...' : 'Delete Permanently'}
            </Button>
         </DialogActions>
      </Dialog>
    </Container>
  );
}
